import React, { useCallback, useEffect, useState } from "react";
import { FiX, FiRefreshCw } from "react-icons/fi";
import toast from "react-hot-toast";
import { fetchTaskHistory } from "../../../utils/taskApi";
import { formatDuration } from "../taskConstants";
import TimeCell from "./TimeCell";

/** One task's full completion history: total count, day-wise counts, and the records. */
const TaskHistoryModal = ({ isOpen, onClose, task }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const load = useCallback(async () => {
    if (!task) return;
    setLoading(true);
    try {
      const res = await fetchTaskHistory(task.id, { page, limit: 20 });
      setData(res.data);
      setLastPage(res.data?.pagination?.total_pages || 1);
    } catch (err) {
      toast.error(err.message || "Failed to load task history");
    } finally {
      setLoading(false);
    }
  }, [task, page]);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
    }
  }, [isOpen, task?.id]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  if (!isOpen || !task) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Completion History</h2>
            <p className="text-xs text-gray-400 mt-0.5">{task.title}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition"
              title="Refresh"
            >
              <FiRefreshCw size={14} className={loading ? "animate-spin text-blue-600" : ""} />
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition">
              <FiX size={17} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading && !data ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Completed</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{data?.total_completed ?? 0}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reward</p>
                  <p className="text-2xl font-bold text-violet-600 mt-1">
                    {task.reward_points} <span className="text-sm text-gray-400 font-medium">PTS</span>
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Days With Activity</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{data?.day_wise?.length ?? 0}</p>
                </div>
              </div>

              {data?.day_wise?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2">Day-wise Completions</p>
                  <div className="flex flex-wrap gap-2">
                    {data.day_wise.map((d) => (
                      <span
                        key={d.date}
                        className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-[11px] font-medium border border-blue-100"
                      >
                        {d.date}: {d.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">Records</p>
                <div className="overflow-x-auto border border-gray-100 rounded-xl">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-left text-[10px] text-gray-400 uppercase tracking-wide">
                        <th className="px-4 py-2.5">User</th>
                        <th className="px-4 py-2.5">Event</th>
                        <th className="px-4 py-2.5">Points</th>
                        <th className="px-4 py-2.5">Started At</th>
                        <th className="px-4 py-2.5">Completed At</th>
                        <th className="px-4 py-2.5" title="From opening the task to completing it">
                          Duration
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(data?.history || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-gray-400">
                            No completions yet.
                          </td>
                        </tr>
                      ) : (
                        data.history.map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50/70">
                            <td className="px-4 py-2.5">
                              <p className="font-medium text-gray-800">{row.username || "—"}</p>
                              <p className="text-[10px] text-gray-400">{row.user_email}</p>
                            </td>
                            <td className="px-4 py-2.5 text-gray-500">#{row.event_id ?? "—"}</td>
                            <td className="px-4 py-2.5">
                              <span className="inline-block bg-violet-50 text-violet-600 font-semibold px-2 py-0.5 rounded-full">
                                +{row.reward_points} PTS
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <TimeCell value={row.visited_at} />
                            </td>
                            <td className="px-4 py-2.5">
                              <TimeCell
                                value={row.completed_at}
                                empty={row.status === "started" ? "Not finished" : "—"}
                              />
                            </td>
                            <td className="px-4 py-2.5 text-gray-700 font-medium tabular-nums whitespace-nowrap">
                              {formatDuration(row.duration_seconds)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-end gap-1.5 mt-3 text-xs text-gray-500">
                    <button
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Previous
                    </button>
                    <span className="px-2 font-medium">
                      Page {page} of {lastPage}
                    </span>
                    <button
                      disabled={page >= lastPage || loading}
                      onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskHistoryModal;
